import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './App';
import { validateEnv } from './utils/env.util';
import { installGlobalErrorReporting } from './services/logs/logs.service';
import './theme/index.scss';

// Summary: Injects a <link rel="preconnect"> for the backend origin before React mounts so the very first XHR (login or auth/me) skips the...
// Details: docs/function-library/web/contexts-pages.md#index
const warmBackendConnection = (): void => {
    try {
        const { VITE_BACKEND_LOCATION } = validateEnv();
        const origin = new URL(VITE_BACKEND_LOCATION).origin;
        if (typeof document === 'undefined' || document.head.querySelector(`link[rel="preconnect"][href="${origin}"]`)) {
            return;
        }
        const link = document.createElement('link');
        link.rel = 'preconnect';
        link.href = origin;
        link.crossOrigin = 'anonymous';
        document.head.appendChild(link);
    } catch {
        // Preconnect is a non-essential perf hint. Never block app boot on it.
    }
};

warmBackendConnection();
installGlobalErrorReporting();

// Summary: Registers a lightweight image cache SW so UI icons/backgrounds and signed S3 images stay warm between tab switches.
const registerImageCacheWorker = (): void => {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
        return;
    }
    if (!('serviceWorker' in navigator)) {
        return;
    }
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    if (!window.isSecureContext && !isLocalhost) {
        return;
    }

    void navigator.serviceWorker.register('/image-cache-sw.js', { scope: '/' }).catch(() => {
        // Non-blocking perf enhancement only.
    });
};

registerImageCacheWorker();

const container = document.getElementById('root') as HTMLElement;
const root = createRoot(container); 
root.render(
    <StrictMode>
        <App />
    </StrictMode>
);
